import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "../styles/projLoginPage.css";
import { API_BASE } from "../config";
import apiFetch from "../apiFetch";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState("validating"); // validating | valid | invalid | expired | done
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }

    apiFetch(`/api/auth/reset-token/${token}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.expired) setStatus("expired");
        else if (data.valid) setStatus("valid");
        else setStatus("invalid");
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return setError("Password must be at least 8 characters.");
    if (newPassword !== confirmPassword) return setError("Passwords do not match.");
    setError("");
    setIsLoading(true);

    try {
      const res = await apiFetch(`/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await res.json();
      if (data.success) setStatus("done");
      else setError(data.message || "Failed to reset password.");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (status === "validating") {
    return (
      <div className="lp-container">
        <div className="lp-card">
          <div className="fp-status">
            <p className="lp-subtitle">Validating your reset link…</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "expired") {
    return (
      <div className="lp-container">
        <div className="lp-card">
          <div className="fp-status">
            <div className="fp-status-icon fp-icon-warn">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="lp-title">Link Expired</h2>
            <p className="lp-subtitle" style={{ textAlign: "center" }}>
              This reset link has expired. Please request a new one.
            </p>
            <p className="fp-back-link" style={{ marginTop: "1.5rem" }}>
              <Link to="/forgot-password">Request new link</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div className="lp-container">
        <div className="lp-card">
          <div className="fp-status">
            <div className="fp-status-icon fp-icon-warn">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="lp-title">Invalid Link</h2>
            <p className="lp-subtitle" style={{ textAlign: "center" }}>
              This reset link is invalid or has already been used.
            </p>
            <p className="fp-back-link" style={{ marginTop: "1.5rem" }}>
              <Link to="/forgot-password">Request new link</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "done") {
    return (
      <div className="lp-container">
        <div className="lp-card">
          <div className="fp-status">
            <div className="fp-status-icon fp-icon-success">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="lp-title">Password Reset!</h2>
            <p className="lp-subtitle" style={{ textAlign: "center" }}>
              Your password has been updated. You can now sign in.
            </p>
            <p className="fp-back-link" style={{ marginTop: "1.5rem" }}>
              <Link to="/projLoginPage">Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // status === "valid" — show the form
  return (
    <div className="lp-container">
      <div className="lp-card">
        <div className="lp-brand">
          <div className="lp-logo">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="lp-title">New Password</h1>
          <p className="lp-subtitle">Choose a strong password</p>
        </div>
        <form className="lp-form" onSubmit={handleSubmit} noValidate>
          {error && <div className="lp-error-banner">{error}</div>}
          <div className="lp-field">
            <label htmlFor="rp-new-pw">New password</label>
            <div className="lp-input-wrap">
              <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <input id="rp-new-pw" type={showPassword ? "text" : "password"}
                placeholder="At least 8 characters"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password" />
              <button type="button" className="lp-eye-btn"
                onClick={() => setShowPassword((v) => !v)} tabIndex={-1}>
                {showPassword
                  ? <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  : <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                }
              </button>
            </div>
          </div>
          <div className="lp-field">
            <label htmlFor="rp-confirm-pw">Confirm password</label>
            <div className="lp-input-wrap">
              <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <input id="rp-confirm-pw" type={showPassword ? "text" : "password"}
                placeholder="Repeat password"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password" />
            </div>
          </div>
          <button type="submit" className="lp-submit" disabled={isLoading}>
            {isLoading ? "Saving…" : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
